import { z } from 'zod';
import { captchaTokenSchema } from './captcha.js';
/**
 * The password-reset / change-password contract (S26 Phase 1: forgot password, reset
 * password with a provider code, and change password while signed in).
 */
/**
 * Cognito's allowed "symbol" characters when `requireSymbols: true`, copied verbatim from
 * AWS's password-policy documentation. Spelled out here rather than imported from a Cognito
 * SDK, because `contracts` is dependency-free and may not depend on one.
 */
const COGNITO_SYMBOL_PATTERN = /[\^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`+=]/;
/**
 * The single source of truth for an acceptable NEW password (forgot-password reset and
 * change-password).
 *
 * Mirrored from the Cognito user-pool `passwordPolicy` in
 * `infra/lib/stacks/identity-stack.ts` (`minLength: 12, requireLowercase: true,
 * requireUppercase: true, requireDigits: true, requireSymbols: true`) so a password this
 * schema accepts is never rejected by the provider. THIS SCHEMA AND THAT POLICY MUST BE
 * CHANGED TOGETHER — there is no runtime link between them.
 *
 * Capped at 256, comfortably under Cognito's own 256-character hard limit, so this schema
 * alone bounds the value before it ever reaches the provider.
 */
export const passwordSchema = z
    .string()
    .min(12, { message: 'must be at least 12 characters' })
    .max(256)
    .regex(/[a-z]/, { message: 'must contain a lowercase letter' })
    .regex(/[A-Z]/, { message: 'must contain an uppercase letter' })
    .regex(/[0-9]/, { message: 'must contain a digit' })
    .regex(COGNITO_SYMBOL_PATTERN, { message: 'must contain a symbol' });
export const forgotPasswordRequestSchema = z.object({
    /** Same email rule as `loginRequestSchema.email`. */
    email: z.string().email().max(320),
    /** Bot-protection token from the form's captcha widget (issue #150). */
    captchaToken: captchaTokenSchema,
});
export const resetPasswordRequestSchema = z.object({
    email: z.string().email().max(320),
    /**
     * The provider's confirmation code, delivered out of band (email today). Treated as an
     * opaque short string — no format assumption beyond "short and non-empty" — because the
     * provider owns its own code shape and `contracts` must not encode a Cognito-specific
     * pattern that a future provider swap would break.
     */
    code: z.string().trim().min(1).max(16),
    newPassword: passwordSchema,
});
export const changePasswordRequestSchema = z.object({
    /**
     * Same permissive rule as `loginRequestSchema.password` — this is a password the user
     * already has, so there is nothing to re-validate against the policy.
     */
    currentPassword: z.string().min(1).max(512),
    newPassword: passwordSchema,
});
//# sourceMappingURL=password.js.map